import { useState } from "react";
import styles from "./LoginForm.module.css";
import { User, Lock, Eye, EyeOff, Mail, Phone, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { signup, enableUser } from "../../services/api";
import T from '../T';

const SignupForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    email: "",
    password: "",
    phone_number: "",
    country: "",
  });

  const countryCodes = {
    NG: "+234",
    GH: "+233",
    KE: "+254",
    UG: "+256",
    CI: "+225",
    SN: "+221",
    ZA: "+27",
    MA: "+212",
    EG: "+20",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      // Auto-apply country code when country is selected
      if (name === "country" && value) {
        const code = countryCodes[value];
        if (!newData.phone_number) {
          newData.phone_number = code;
        } else if (
          prev.country &&
          countryCodes[prev.country] &&
          newData.phone_number.startsWith(countryCodes[prev.country])
        ) {
          newData.phone_number = newData.phone_number.replace(
            countryCodes[prev.country],
            code,
          );
        } else if (!newData.phone_number.startsWith("+")) {
          // If they already started typing without a plus, prefix it (strip leading zero just in case)
          newData.phone_number = code + newData.phone_number.replace(/^0/, "");
        }
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Email domain validation
    const allowedDomains = [
      "@autochek.africa",
      "@coinafrique.com",
      "@kifal.ma",
    ];
    const email = formData.email.toLowerCase();
    const isStaffEmail = allowedDomains.some((domain) =>
      email.endsWith(domain),
    );

    if (!isStaffEmail) {
      toast.error(
        "We could not verify your staff status. Please use an official Autochek, CoinAfrique, or Kifal email address to create an account.",
      );
      return;
    }

    // Clean payload for the API
    const payload = { ...formData };
    if (!payload.middle_name || payload.middle_name.trim() === "") {
      delete payload.middle_name;
    }

    // Final check for phone number format
    if (payload.country && !payload.phone_number.startsWith("+")) {
      const code = countryCodes[payload.country] || "";
      payload.phone_number = code + payload.phone_number.replace(/^0/, "");
    }

    setIsLoading(true);
    try {
      const response = await signup(payload);

      // Save the signup response to local storage
      localStorage.setItem("signup_response", JSON.stringify(response));

      // Background enable user api call
      if (response && response.data && response.data.id) {
        await enableUser(response.data.id);
      }

      // Show success popup
      setShowPopup(true);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Signup error:", error);
      }
      toast.error(
        error instanceof Error
          ? error.message
          : "Signup failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}><T>Sign Up</T></h2>
        <p className={styles.subtitle}><T>Create your staff account.</T></p>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* First Name & Last Name row */}
        <div style={{ display: "flex", gap: "1rem" }}>
          <div className={styles.inputGroup} style={{ flex: 1 }}>
            <div className={styles.iconWrapper}>
              <User size={16} />
            </div>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="First Name"
              className={styles.input}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.inputGroup} style={{ flex: 1 }}>
            <div className={styles.iconWrapper}>
              <User size={16} />
            </div>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Last Name"
              className={styles.input}
              required
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Middle Name */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <User size={16} />
          </div>
          <input
            type="text"
            name="middle_name"
            value={formData.middle_name}
            onChange={handleChange}
            placeholder="Middle Name (Optional)"
            className={styles.input}
            disabled={isLoading}
          />
        </div>

        {/* Email */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Mail size={16} />
          </div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.name@autochek.africa"
            className={styles.input}
            required
            disabled={isLoading}
          />
        </div>

        {/* Country */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Globe size={16} />
          </div>
          <select
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={styles.input}
            required
            disabled={isLoading}
          >
            <option value="" disabled>
              Select Country
            </option>
            <option value="NG"><T>Nigeria</T></option>
            <option value="GH"><T>Ghana</T></option>
            <option value="KE"><T>Kenya</T></option>
            <option value="UG"><T>Uganda</T></option>
            <option value="CI"><T>Ivory Coast</T></option>
            <option value="SN"><T>Senegal</T></option>
            <option value="ZA"><T>South Africa</T></option>
            <option value="MA"><T>Morocco</T></option>
            <option value="EG"><T>Egypt</T></option>
          </select>
        </div>

        {/* Phone Number */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Phone size={16} />
          </div>
          <input
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="Phone Number"
            className={styles.input}
            required
            disabled={isLoading}
          />
        </div>

        {/* Password */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Lock size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
            className={styles.input}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            className={styles.eyeToggle}
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <button
          type="submit"
          className={styles.signInButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <T>Creating account...</T>
          ) : (
            <>
              <T>Sign Up</T> <span className={styles.arrow}>→</span>
            </>
          )}
        </button>

        <div
          style={{
            marginTop: "1.5rem",
            textAlign: "center",
            fontSize: "0.9rem",
            color: "#6b7280",
          }}
        >
          <T>Already have an account?</T>{" "}
          <span
            onClick={() => navigate("/staff")}
            style={{ color: "#f5a623", cursor: "pointer", fontWeight: "500" }}
          >
            <T>Login</T>
          </span>
        </div>
      </form>

      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "400px",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#111827", fontSize: "1.25rem" }}>
              <T>Account Created!</T>
            </h3>
            <p
              style={{
                color: "#4b5563",
                lineHeight: "1.6",
                marginTop: "1rem",
                marginBottom: "1.5rem",
                fontSize: "0.95rem",
              }}
            >
              <T>Please check your email. A verification link has just been sent to your email and will expire after 24 hours. After verifying your email, you can come back to this tab to login, and create your ticket.</T>
            </p>
            <button
              onClick={() => {
                setShowPopup(false);
                navigate("/staff");
              }}
              style={{
                backgroundColor: "#f5a623",
                color: "#111827",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
                width: "100%",
                fontSize: "1rem",
              }}
            >
              <T>Okay</T>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupForm;
